import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import {
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Layout from '../components/Layout';
import adminService from '../services/adminService';
import apartmentService from '../services/apartmentService';
import { useUnion } from '../contexts/UnionContext';
import { useApartment } from '../contexts/ApartmentContext';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function MeterReadings() {
  const { t } = useTranslation();
  usePageTitle(t('meterReadings.title'));
  const { activeUnion } = useUnion();
  const { activeApartment } = useApartment();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [utilityTypes, setUtilityTypes] = useState([]);
  const [filterUtility, setFilterUtility] = useState('');

  useEffect(() => {
    if (!activeUnion) return;
    apartmentService.getUtilityTypes()
      .then(data => setUtilityTypes(
        (Array.isArray(data) ? data : data.results || []).filter(u => u.calculation_method === 'BY_CONSUMPTION')
      ))
      .catch(() => {});
  }, [activeUnion]);

  const loadReadings = useCallback(() => {
    if (!activeUnion) return;
    setLoading(true);
    setError('');

    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

    const params = { start_date: startDate, end_date: endDate };
    if (activeApartment) params.apartment = activeApartment.id;
    if (filterUtility) params.utility_type = filterUtility;

    adminService.getMeterReadings(params)
      .then(data => setReadings(Array.isArray(data) ? data : data.results || []))
      .catch(err => setError(t('meterReadings.failedToLoad', { status: err.response?.status ?? 'network error' })))
      .finally(() => setLoading(false));
  }, [activeUnion, activeApartment, year, month, filterUtility, t]);

  useEffect(() => {
    loadReadings();
  }, [loadReadings]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 12 ? new Date(year + 1, 0, 1) > now : new Date(year, month, 1) > now) return;
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>{t('meterReadings.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('meterReadings.subtitle')}
          {activeApartment ? ` · ${activeApartment.unit_number} ${t('meterReadings.apt')}` : ''}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardHeader
          avatar={<SpeedIcon color="primary" />}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={t('meterReadings.prevMonth')}>
                <IconButton size="small" onClick={prevMonth}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="h6" component="span">
                {MONTH_NAMES[month]} {year}
              </Typography>
              <Tooltip title={t('meterReadings.nextMonth')}>
                <span>
                  <IconButton size="small" onClick={nextMonth} disabled={isCurrentMonth}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          }
          subheader={`${readings.length} ${t('meterReadings.readingsFound')}`}
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('apartments.year')}</InputLabel>
                <Select label={t('apartments.year')} value={year} onChange={e => setYear(Number(e.target.value))}>
                  {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('apartments.month')}</InputLabel>
                <Select label={t('apartments.month')} value={month} onChange={e => setMonth(Number(e.target.value))}>
                  {MONTH_NAMES.slice(1).map((name, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {utilityTypes.length > 1 && (
              <Grid item xs={12} sm={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('meterReadings.utilityType')}</InputLabel>
                  <Select label={t('meterReadings.utilityType')} value={filterUtility} onChange={e => setFilterUtility(e.target.value)}>
                    <MenuItem value="">{t('meterReadings.allTypes')}</MenuItem>
                    {utilityTypes.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[0, 1, 2, 3, 4].map(i => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
          ) : readings.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                {t('meterReadings.noReadings', { month: MONTH_NAMES[month], year })}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
                    <TableCell>{t('meterReadings.date')}</TableCell>
                    <TableCell>{t('meterReadings.apartment')}</TableCell>
                    <TableCell>{t('meterReadings.utilityType')}</TableCell>
                    <TableCell align="right">{t('meterReadings.reading')}</TableCell>
                    <TableCell>{t('meterReadings.submittedBy')}</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {readings.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Chip
                          label={new Date(r.reading_date).toLocaleDateString('et-EE')}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: 12 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {r.apartment_number} {t('meterReadings.apt')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.utility_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontFamily="monospace">{r.value}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {r.submitted_by_username || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.notes || ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}

export default MeterReadings;
